import { t, Trans } from '@lingui/macro';
import { Box, Button, Card, CardActions, CardContent, Grid, Typography } from '@material-ui/core';
import { ArrowForward } from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';

import AdminPage from '../AdminPage';
import Actions from '../../page-framework/actions/Actions';
import { AdminPages, buildPageId, buildRootPageTarget } from '../AdminLinkUtils';
import Link from '../../links/Link';

const useStyles = makeStyles((theme) => ({
    overviewContainer: {
        maxWidth: 1233,
        marginTop: theme.spacing(2),
    },
    card: {
        border: 'none',
        width: '100%',
        minHeight: 150,
    },
    cardContent: {
        minHeight: 100,
    },
    descriptionText: {
        fontSize: theme.typography.pxToRem(13),
        lineHeight: 1.4,
    },
    linkButton: {
        backgroundColor: theme.palette.type === 'light' ? '#eee' : 'rgba(255, 255, 255, 0.08)',
    },
}));

function VoipEntryCard({ title, description, actionLabel, to, onClick, external = false }) {
    const classes = useStyles();
    const buttonProps = external ? { onClick } : { component: Link, to };

    return (
        <Card variant="outlined" className={classes.card}>
            <CardContent className={classes.cardContent}>
                <Typography variant="h6">{title}</Typography>
                <Box mt={2}>
                    <Typography variant="body2" className={classes.descriptionText}>
                        {description}
                    </Typography>
                </Box>
            </CardContent>
            <CardActions>
                <Button className={classes.linkButton} {...buttonProps} endIcon={<ArrowForward />}>
                    {actionLabel}
                </Button>
            </CardActions>
        </Card>
    );
}

function VoipOverviewContent() {
    const classes = useStyles();

    return (
        <Box className={classes.overviewContainer}>
            <Box mb={3}>
                <Typography variant="h5">
                    <Trans>VoIP Testing with nGeniusPULSE</Trans>
                </Typography>
                <Box mt={2}>
                    <Typography variant="subtitle1">
                        <Trans>
                            An nPoint can register as a phone with your call manager and then place or receive calls in
                            order to measure the quality and availability of your VoIP infrastructure. For each call,
                            nGeniusPULSE displays the measured results for MOS, Jitter, Latency, Loss, and other key
                            performance data.
                        </Trans>
                    </Typography>
                </Box>
            </Box>

            <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                    <VoipEntryCard
                        title={<Trans>Step 1 - Call Managers</Trans>}
                        description={<Trans>Set up the Call Manager(s) that your nPoints will register with.</Trans>}
                        actionLabel={<Trans>GO TO CALL MANAGERS</Trans>}
                        to={`${buildRootPageTarget(AdminPages.VoipConfig)}/callManagers`}
                    />
                </Grid>
                <Grid item xs={12} md={4}>
                    <VoipEntryCard
                        title={<Trans>Step 2 - VoIP Thresholds</Trans>}
                        description={
                            <Trans>
                                Set the limits for VoIP test MOS scores. These are the ratings you will see in the VoIP
                                test results summary.
                            </Trans>
                        }
                        actionLabel={<Trans>GO TO VOIP THRESHOLDS</Trans>}
                        to={`${buildRootPageTarget(AdminPages.Thresholds)}?type=VoIP`}
                    />
                </Grid>
                <Grid item xs={12} md={4}>
                    <VoipEntryCard
                        title={<Trans>Step 3 - nPoint Configuration</Trans>}
                        description={
                            <Trans>
                                Configure your nPoint to match the settings for VoIP phones in your Call Manager.
                            </Trans>
                        }
                        actionLabel={<Trans>GO TO NPOINT CONFIGURATIONS</Trans>}
                        to={`${buildRootPageTarget(AdminPages.VoipConfig)}/list`}
                    />
                </Grid>
            </Grid>
        </Box>
    );
}

VoipOverviewContent.displayName = 'VoipOverviewContent';

export default function VoipAdminPage() {
    return (
        <AdminPage title={t`VoIP Configuration`} pageId={buildPageId(AdminPages.VoipConfig)} actions={<Actions />}>
            <VoipOverviewContent />
        </AdminPage>
    );
}
